import React, { createContext, useContext, useState, useEffect } from 'react';
import { detectUserCurrency, isSupportedCurrency } from '@/utils/locationDetection';

export interface Currency {
  code: string;
  name: string;
  symbol: string;
  rate: number; // Rate relative to USD
}

export const currencies: Currency[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$', rate: 1 },
  { code: 'EUR', name: 'Euro', symbol: '€', rate: 0.92 },
  { code: 'GBP', name: 'British Pound', symbol: '£', rate: 0.79 },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'ج.م', rate: 49.81287 }, // Updated to current market rate
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', rate: 1.35 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', rate: 1.50 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', rate: 150 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', rate: 7.25 },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', rate: 83 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', rate: 3.75 },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', rate: 3.67 }
];

interface CurrencyContextType {
  selectedCurrency: Currency;
  setSelectedCurrency: (currency: Currency) => void;
  convertPrice: (usdPrice: number) => number;
  formatPrice: (usdPrice: number) => string;
  isLoadingRates: boolean;
  lastUpdated: Date | null;
  currencies: Currency[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Default to EGP (Egyptian Pound)
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(currencies.find(c => c.code === 'EGP') || currencies[0]);
  const [currencyRates, setCurrencyRates] = useState<Currency[]>(currencies);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [hasAutoDetected, setHasAutoDetected] = useState(false);

  // Fetch real-time exchange rates from multiple sources
  const fetchExchangeRates = async () => {
    setIsLoadingRates(true);
    try {
      
      // Try multiple APIs for better accuracy
      let data = null;
      
      // Try exchangerate-api.com first
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD', {
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (response.ok) {
          data = await response.json();
        }
      } catch (error) {
        // Continue to fallback
      }

      // Use updated market rates as fallback (more accurate than old static rates)
      const currentMarketRates = {
        USD: 1,
        EUR: 0.9234, // Updated from market data
        GBP: 0.7891, // Updated from market data
        EGP: 49.81287, // Current market rate you provided
        CAD: 1.3542, // Updated
        AUD: 1.4987, // Updated
        JPY: 149.85, // Updated
        CNY: 7.2456, // Updated
        INR: 83.15, // Updated
        SAR: 3.7507, // Updated (pegged to USD)
        AED: 3.6731 // Updated (pegged to USD)
      };

      let ratesToUse = currentMarketRates;

      if (data && data.rates) {
        // Use API rates if available, otherwise use our updated market rates
        ratesToUse = {
          USD: 1,
          EUR: data.rates.EUR || currentMarketRates.EUR,
          GBP: data.rates.GBP || currentMarketRates.GBP,
          EGP: data.rates.EGP || currentMarketRates.EGP,
          CAD: data.rates.CAD || currentMarketRates.CAD,
          AUD: data.rates.AUD || currentMarketRates.AUD,
          JPY: data.rates.JPY || currentMarketRates.JPY,
          CNY: data.rates.CNY || currentMarketRates.CNY,
          INR: data.rates.INR || currentMarketRates.INR,
          SAR: data.rates.SAR || currentMarketRates.SAR,
          AED: data.rates.AED || currentMarketRates.AED
        };
      }
      
      const updatedCurrencies = currencies.map(currency => ({
        ...currency,
        rate: ratesToUse[currency.code as keyof typeof ratesToUse] || currency.rate
      }));
      
      setCurrencyRates(updatedCurrencies);
      setLastUpdated(new Date());
      
      // Update selected currency with new rate
      const updatedSelectedCurrency = updatedCurrencies.find(c => c.code === selectedCurrency.code);
      if (updatedSelectedCurrency) {
        setSelectedCurrency(updatedSelectedCurrency);
      }
      
    } catch (error) {
      // Keep existing rates if all fails
    } finally {
      setIsLoadingRates(false);
    }
  };

  // Auto-detect currency based on user location, then load saved preference
  useEffect(() => {
    const initializeCurrency = async () => {
      if (hasAutoDetected) return;
      
      try {
        // First check if user has manually selected a currency before
        const savedCurrencyCode = localStorage.getItem('selectedCurrency');
        const hasManualSelection = localStorage.getItem('manualCurrencySelection') === 'true';
        
        if (hasManualSelection && savedCurrencyCode) {
          // User has manually selected a currency, use that
          const savedCurrency = currencyRates.find(c => c.code === savedCurrencyCode);
          if (savedCurrency) {
            setSelectedCurrency(savedCurrency);
            setHasAutoDetected(true);
            return;
          }
        }
        
        // Auto-detect currency based on location
        console.log('Auto-detecting currency based on location...');
        const detectedCurrencyCode = await detectUserCurrency();
        console.log('Detected currency:', detectedCurrencyCode);
        
        if (isSupportedCurrency(detectedCurrencyCode)) {
          const detectedCurrency = currencyRates.find(c => c.code === detectedCurrencyCode);
          if (detectedCurrency) {
            console.log('Setting currency to:', detectedCurrency);
            setSelectedCurrency(detectedCurrency);
            // Save the auto-detected currency but don't mark as manual selection
            localStorage.setItem('selectedCurrency', detectedCurrency.code);
          }
        } else {
          // Fallback to USD if currency not supported
          console.log('Currency not supported, falling back to USD');
          const usdCurrency = currencyRates.find(c => c.code === 'USD');
          if (usdCurrency) {
            setSelectedCurrency(usdCurrency);
            localStorage.setItem('selectedCurrency', 'USD');
          }
        }
      } catch (error) {
        console.log('Auto-detection failed, using USD:', error);
        // Ultimate fallback to USD
        const usdCurrency = currencyRates.find(c => c.code === 'USD');
        if (usdCurrency) {
          setSelectedCurrency(usdCurrency);
          localStorage.setItem('selectedCurrency', 'USD');
        }
      } finally {
        setHasAutoDetected(true);
      }
    };

    if (currencyRates.length > 0) {
      initializeCurrency();
    }
  }, [currencyRates, hasAutoDetected]);

  // Fetch rates on mount and periodically
  useEffect(() => {
    fetchExchangeRates();
    
    // Update rates every hour
    const interval = setInterval(fetchExchangeRates, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const handleCurrencyChange = (currency: Currency) => {
    // Always get the latest rate from currencyRates
    const updatedCurrency = currencyRates.find(c => c.code === currency.code) || currency;
    setSelectedCurrency(updatedCurrency);
    localStorage.setItem('selectedCurrency', updatedCurrency.code);
    // Mark as manual selection so auto-detection won't override it
    localStorage.setItem('manualCurrencySelection', 'true');
  };

  const convertPrice = (usdPrice: number): number => {
    const convertedPrice = usdPrice * selectedCurrency.rate;
    return convertedPrice;
  };

  const formatPrice = (usdPrice: number): string => {
    const convertedPrice = convertPrice(usdPrice);
    
    // For EGP, round to nearest multiple of 10
    if (selectedCurrency.code === 'EGP') {
      const roundedToTens = Math.round(convertedPrice / 10) * 10;
      // Return empty string if price rounds to 0 or less
      if (roundedToTens <= 0) return '';
      return `${selectedCurrency.symbol}${roundedToTens}`;
    }
    
    // For other currencies, round to the nearest tenth (one decimal place)
    // Only show decimal if it's not .0
    const roundedPrice = Math.round(convertedPrice * 10) / 10;
    // Return empty string if price rounds to 0 or less
    if (roundedPrice <= 0) return '';
    const formatted = roundedPrice % 1 === 0 
      ? `${selectedCurrency.symbol}${Math.round(roundedPrice)}`
      : `${selectedCurrency.symbol}${roundedPrice.toFixed(1)}`;
    return formatted;
  };

  return (
    <CurrencyContext.Provider value={{
      selectedCurrency,
      setSelectedCurrency: handleCurrencyChange,
      convertPrice,
      formatPrice,
      isLoadingRates,
      lastUpdated,
      currencies: currencyRates
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
